import { RoleDTO } from "@js/DTO/RoleDTO.cs.d";
import { BaseService } from "@js/filesfromccqbase/BaseService";
import { BroadcastService } from "@js/filesfromccqbase/BroadcastService";
import { ClaimGroup, RoleClient } from "@src/apis/ModularClient";
import { IndexService } from "@js/services/IndexService";
export class RoleService extends BaseService<RoleDTO, string> {
  userroles: string[] = null;
  public IsAdminUser: boolean = false;
  public IsLenderUserOrAbove: boolean = false;
  public IsBrokerUserOrAbove: boolean = false;
  public IsClientUserOrAbove: boolean = false;
  public IsLender: boolean = false;
  public IsIntroducer: boolean = false;
  public IsClient: boolean = false;
  public IsLenderVisible: boolean = false;
  public IsBroker: boolean = false;
  public IsOrganisationAdmin: boolean = false;
  public isTestBannerHidden: boolean = false;

  static $inject = [
    "$http",
    "$cookies",
    "$rootScope",
    "$q",
    "$timeout",
    "BroadcastService",
    "$location",
    "IndexService",
  ];

  constructor(
    http: ng.IHttpService,
    cookies: ng.cookies.ICookiesService,
    private rootScope: ng.IRootScopeService,
    q: ng.IQService,
    timeout: ng.ITimeoutService,
    broadcastservice: BroadcastService,
    private $location: ng.ILocationService,
    private indexService: IndexService,
  ) {
    super(http, cookies, rootScope, q, timeout, broadcastservice);
    this.$baseAddress = "/api/roles";
    this.$broadcastBusinessNameSingular = "Role";
    this.$broadcastBusinessNamePlural = this.$broadcastBusinessNameSingular + "s";
    this.$broadcastScreen = this.$broadcastBusinessNamePlural;
  }

  public UpdateUserRoles(userRoles: string[]): void {
    this.userroles = userRoles;
    this.rootScope.$broadcast("userRolesChange", userRoles);
  }

  public async GetUserRoles(force: boolean = false): Promise<string[]> {
    let defer = this.$q.defer<string[]>();

    if (!this.userroles || force) {
      try {
        const result = await this.indexService.currentUserRoles;
        this.UpdateUserRoles(result);
        // Changed else ifs to ifs because a user can have more than one role!
        if (result.filter((x) => x == "Admin").length > 0) {
          this.IsAdminUser = true;
          this.IsClientUserOrAbove = true;
          this.IsLenderUserOrAbove = true;
        }
        if (result.filter((x) => x == "Lender").length > 0) {
          this.IsLender = true;
          this.IsLenderUserOrAbove = true;
          this.IsClientUserOrAbove = true;
        }
        if (result.filter((x) => x == "Introducer").length > 0) {
          this.IsIntroducer = true;
          this.IsClientUserOrAbove = true;
        }
        if (result.filter((x) => x == "Client").length > 0) {
          this.IsClient = true;
          this.IsClientUserOrAbove = true;
        }
        if (result.filter((x) => x == "Broker").length > 0) {
          this.IsBroker = true;
          this.IsBrokerUserOrAbove = true;
        }
        defer.resolve(result);
      } catch (e) {
        this.UpdateUserRoles([]);
        defer.reject(false);
      }
    } else {
      defer.resolve(this.userroles);
    }

    return defer.promise;
  }
  async hasRole(role: string): Promise<boolean> {
    const roles = await this.indexService.currentUserRoles;
    return roles.includes(role);
  }

  async hasAnyRole(rolesToCheck: string[]): Promise<boolean> {
    const roles = await this.indexService.currentUserRoles;
    return roles.some((role) => rolesToCheck.includes(role));
  }

  async hasOnlyRoles(allowedRoles: string[]): Promise<boolean> {
    const roles = await this.indexService.currentUserRoles;
    const userRolesSet = new Set(roles);
    const allowedRolesSet = new Set(allowedRoles);

    if (userRolesSet.size !== allowedRolesSet.size) return false;
    return [...userRolesSet].every((role) => allowedRolesSet.has(role));
  }

  async isAdminUser(): Promise<boolean> {
    return this.hasRole("Admin");
  }

  async isBrokerOrABove(): Promise<boolean> {
    return this.hasAnyRole(["Broker", "Admin"]);
  }

  async isBroker(): Promise<boolean> {
    return this.hasRole("Broker");
  }

  async isIntroducer(): Promise<boolean> {
    return this.hasAnyRole(["Introducer", "Admin"]);
  }
  async isClientUser(): Promise<boolean> {
    return this.hasRole("Client");
  }

  async isClientUserOrAbove(): Promise<boolean> {
    return this.hasAnyRole(["Client", "Admin"]);
  }

  async isLenderUserOrAbove(): Promise<boolean> {
    return this.hasAnyRole(["Lender", "Admin"]);
  }

  async isLenderUser(): Promise<boolean> {
    return this.hasRole("Lender");
  }

  async isIntroducerOnly(): Promise<boolean> {
    return this.hasOnlyRoles(["Introducer"]);
  }

  async isOrganiationAdminUser(): Promise<boolean> {
    return this.hasRole("OrganisationAdmin");
  }

  goHomeBasedOnUser() {
    this.isAdminUser().then((result) => {
      if (result) {
        this.$location.path("/dashboard");
      } else {
        this.$location.path("/userdashboard");
      }
    });
  }

  getIsLenderVisible() {
    return this.IsLenderVisible;
  }

  setIsLenderVisible(isLenderVisible: boolean) {
    this.IsLenderVisible = isLenderVisible;
  }

  resetUserRoles() {
    this.userroles = null;
    this.IsAdminUser = false;
    this.IsLenderUserOrAbove = false;
    this.IsBrokerUserOrAbove = false;
    this.IsClientUserOrAbove = false;
    this.IsLender = false;
    this.IsIntroducer = false;
    this.IsClient = false;
    this.IsLenderVisible = false;
    this.IsBroker = false;
    this.IsOrganisationAdmin = false;
  }
}
